/**
 * ProgressSection — Barra de progresso + arquivo atual para tarefas de import/export
 */
import React from 'react';
import { useBigwaStatusPanelStore } from '../../stores/bigwaStatusPanelStore';
import type { StatusTask } from '../../stores/bigwaStatusPanelStore';
import { CloseIcon } from '../bigwa-icons';

const DEFAULT_TASK_ID = '__default__';

// ═══════════════════════════════════════════════════════════════
// SPINNER
// ═══════════════════════════════════════════════════════════════

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" style={{ animation: 'bsp-spin 1s linear infinite', flexShrink: 0 }}>
      <circle cx="8" cy="8" r="6" fill="none" stroke="#477B72" strokeWidth="2" strokeDasharray="28" strokeDashoffset="8" strokeLinecap="round" />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// CLOSE BUTTON
// ═══════════════════════════════════════════════════════════════

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 2,
        display: 'flex',
        color: '#6b7280',
      }}
    >
      <CloseIcon size={14} variant="dark" />
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// PROGRESS SECTION (task content)
// ═══════════════════════════════════════════════════════════════

export function ProgressSection() {
  const task = useBigwaStatusPanelStore(s => s.tasks.find(t => t.id === DEFAULT_TASK_ID));

  if (!task) return null;

  return task.mode === 'export' ? <ExportContent task={task} /> : <ImportContent task={task} />;
}

// ─── Export Content ──────────────────────────────────────────

function ExportContent({ task }: { task: StatusTask }) {
  const closeTask = useBigwaStatusPanelStore(s => s.closeTask);

  const title = task.isProcessing
    ? 'Exportando para Google Docs'
    : task.exportError
      ? 'Erro na exportação'
      : 'Documento criado';

  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px 10px', flexShrink: 0,
      }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: '#ffffff' }}>{title}</span>
        {task.isProcessing ? <Spinner /> : <CloseButton onClick={() => closeTask()} />}
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0 16px', overflow: 'hidden' }}>
        {task.isProcessing ? (
          <>
            <span style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>Criando documento...</span>
            <span style={{
              fontSize: 12, color: '#6B7280', marginTop: 8,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {task.exportTitle}
            </span>
            <div style={{ flex: 1 }} />
            <div style={{ borderTop: '1px solid #2a2a2a', padding: '10px 0', fontSize: 11, color: '#F2BC41' }}>
              Não feche a janela durante a exportação
            </div>
          </>
        ) : task.exportError ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#D4918A', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#d1d5db' }}>Não foi possível criar o documento</span>
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ borderTop: '1px solid #2a2a2a', padding: '10px 0', fontSize: 11, color: '#D4918A' }}>
              {task.exportError}
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#477B72', flexShrink: 0 }} />
              <span style={{
                fontSize: 13, color: '#d1d5db',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {task.exportTitle}
              </span>
            </div>
            {task.exportUrl && (
              <a
                href={task.exportUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => closeTask()}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12,
                  fontSize: 13, fontWeight: 600, color: '#477B72', textDecoration: 'none',
                  borderBottom: '1px solid rgba(71,123,114,0.3)', alignSelf: 'flex-start',
                }}
              >
                Abrir no Google Docs
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
            )}
            <div style={{ flex: 1 }} />
            <div style={{ borderTop: '1px solid #2a2a2a', padding: '10px 0', fontSize: 11, color: '#F2BC41' }}>
              O documento foi salvo no seu Google Drive.
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ─── Import Content ──────────────────────────────────────────

function ImportContent({ task }: { task: StatusTask }) {
  const closeTask = useBigwaStatusPanelStore(s => s.closeTask);

  const totalImported = task.success + task.associated;
  const progressPercent = task.totalFiles > 0 ? Math.round((task.currentIndex / task.totalFiles) * 100) : 0;
  const hasProblems = task.noMetadata.length > 0 || task.duplicatesSkipped > 0 || task.failures.length > 0;

  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px 10px', flexShrink: 0,
      }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: '#ffffff' }}>
          {task.isProcessing ? 'Importando PDFs' : 'Importação concluída'}
        </span>
        {task.isProcessing ? <Spinner /> : <CloseButton onClick={() => closeTask()} />}
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0 16px', overflow: 'hidden' }}>
        {task.isProcessing ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: '#9CA3AF' }}>Processando {task.currentIndex} de {task.totalFiles}...</span>
              <span style={{ fontSize: 12, color: '#9CA3AF' }}>{progressPercent}%</span>
            </div>
            <div style={{ width: '100%', height: 3, backgroundColor: '#333', borderRadius: 2, overflow: 'hidden', marginBottom: 10 }}>
              <div style={{
                height: '100%', width: `${progressPercent}%`,
                backgroundColor: '#477B72', borderRadius: 2, transition: 'width 0.3s ease',
              }} />
            </div>
            <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {task.currentFile}
            </span>
            <div style={{ flex: 1 }} />
            <div style={{ borderTop: '1px solid #2a2a2a', padding: '10px 0', fontSize: 11, color: '#F2BC41' }}>
              Não feche a janela durante a importação
            </div>
          </>
        ) : !hasProblems ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#477B72', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#d1d5db' }}>
                {totalImported} referência{totalImported !== 1 ? 's' : ''} importada{totalImported !== 1 ? 's' : ''} com sucesso
              </span>
            </div>
            {task.associated > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#60A5FA', flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: '#d1d5db' }}>
                  {task.associated} associado{task.associated !== 1 ? 's' : ''} a referência{task.associated !== 1 ? 's' : ''} existente{task.associated !== 1 ? 's' : ''}
                </span>
              </div>
            )}
            <div style={{ flex: 1 }} />
            <div style={{ borderTop: '1px solid #2a2a2a', padding: '10px 0', fontSize: 11, color: '#F2BC41' }}>
              Todas as referências foram processadas sem problemas.
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 2 }}>
              {totalImported > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#477B72', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#d1d5db' }}>
                    {totalImported} referência{totalImported !== 1 ? 's' : ''} importada{totalImported !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              {task.noMetadata.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#F2BC41', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#d1d5db' }}>{task.noMetadata.length} sem metadados</span>
                </div>
              )}
              {task.duplicatesSkipped > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#9CA3AF', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#d1d5db' }}>
                    {task.duplicatesSkipped} duplicado{task.duplicatesSkipped !== 1 ? 's' : ''} ignorado{task.duplicatesSkipped !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              {task.failures.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#D4918A', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#d1d5db' }}>{task.failures.length} falha{task.failures.length !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ borderTop: '1px solid #2a2a2a', padding: '8px 0', overflow: 'auto', maxHeight: 60 }}>
              {task.noMetadata.map((name, i) => (
                <div key={`nm-${i}`} style={{ fontSize: 11, color: '#F2BC41', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {name}
                </div>
              ))}
              {task.failures.map((name, i) => (
                <div key={`f-${i}`} style={{ fontSize: 11, color: '#D4918A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {name}
                </div>
              ))}
              {task.noMetadata.length === 0 && task.failures.length === 0 && task.duplicatesSkipped > 0 && (
                <span style={{ fontSize: 11, color: '#F2BC41' }}>
                  Referências duplicadas foram ignoradas automaticamente.
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
